import { useEffect, useState } from "react";
import { ApiError, api } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Skeleton } from "@/components/Skeleton";
import { roleLabel } from "@/types";

interface PageDef {
  key: string;
  label: string;
  default_roles: string[];
  lockable: boolean;
}

interface RolePagesResponse {
  pages: PageDef[];
  roles: string[];
  effective: Record<string, string[]>;
  overrides: Record<string, string[]>;
}


const ROLE_DESCRIPTION: Record<string, string> = {
  operations_manager: "Executive Manager's right hand. Sees the whole agency.",
  lead_broker: "Sees self + direct reports.",
  broker: "Senior agent. Sees only themselves.",
  member: "Basic agent. Sees only themselves.",
};

export function RolePermissions() {
  const { refresh } = useAuth();
  const [data, setData] = useState<RolePagesResponse | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [savingRole, setSavingRole] = useState<string | null>(null);
  const [saveErr, setSaveErr] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await api<RolePagesResponse>("/api/agency/role-pages");
        setData(res);
      } catch (e) {
        setLoadErr(
          e instanceof ApiError
            ? e.message
            : e instanceof Error
              ? e.message
              : "Failed to load",
        );
      }
    })();
  }, []);

  async function toggle(role: string, pageKey: string, def: PageDef) {
    if (!data) return;
    if (!def.lockable) return;
    const current = new Set(data.effective[role] ?? []);
    if (current.has(pageKey)) current.delete(pageKey);
    else current.add(pageKey);
    const allowed = Array.from(current);

    setSavingRole(role);
    setSaveErr(null);
    // Optimistic — patch the in-memory effective set.
    setData({
      ...data,
      effective: { ...data.effective, [role]: allowed },
    });
    try {
      const fresh = await api<RolePagesResponse>(
        "/api/agency/role-pages",
        { method: "PUT", json: { role, allowed } },
      );
      setData(fresh);
      // The viewer's own allowed_pages may have changed (if they're
      // not the owner — but only owners hit this page anyway, so this
      // is a no-op in practice; refreshing is harmless).
      refresh();
    } catch (e) {
      setSaveErr(
        e instanceof ApiError
          ? e.message
          : e instanceof Error
            ? e.message
            : "Save failed",
      );
      // Roll back by re-fetching the canonical state.
      try {
        const res = await api<RolePagesResponse>("/api/agency/role-pages");
        setData(res);
      } catch {
        // Leave optimistic state in place if even the GET fails.
      }
    } finally {
      setSavingRole(null);
    }
  }

  async function resetRole(role: string) {
    if (!confirm(`Reset ${roleLabel(role)} to default pages?`)) return;
    setSavingRole(role);
    setSaveErr(null);
    try {
      const fresh = await api<RolePagesResponse>(
        "/api/agency/role-pages",
        { method: "PUT", json: { role, allowed: [] } },
      );
      setData(fresh);
      refresh();
    } catch (e) {
      setSaveErr(
        e instanceof ApiError
          ? e.message
          : e instanceof Error
            ? e.message
            : "Reset failed",
      );
    } finally {
      setSavingRole(null);
    }
  }

  if (loadErr) {
    return (
      <div className="w-full">
        <div className="card p-4 border-rose-500/25 bg-rose-500/[0.04] text-rose-300 text-sm">
          {loadErr}
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="w-full space-y-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  return (
    <div className="w-full space-y-5">
      <PageHeader
        title="Role permissions"
        subtitle="Choose which pages each role in your agency can see. The Executive Manager role always has full access."
      />

      {saveErr && (
        <div className="card p-3 border-rose-500/25 bg-rose-500/[0.04] text-rose-300 text-sm">
          {saveErr}
        </div>
      )}

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/5">
                <th className="text-left px-4 py-3 text-[11px] uppercase tracking-wider text-slate-500 font-semibold sticky left-0 bg-ink-900 z-10">
                  Page
                </th>
                {data.roles.map((role) => (
                  <th
                    key={role}
                    className="px-3 py-3 text-[11px] uppercase tracking-wider text-slate-500 font-semibold whitespace-nowrap"
                  >
                    <div className="flex flex-col items-center gap-1">
                      <span>{roleLabel(role)}</span>
                      <button
                        type="button"
                        onClick={() => resetRole(role)}
                        disabled={savingRole === role}
                        className="text-[9px] normal-case tracking-normal font-normal text-slate-600 hover:text-accent-300 disabled:opacity-40"
                        title={`Reset ${roleLabel(role)} to defaults`}
                      >
                        reset
                      </button>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.pages.map((p) => (
                <tr key={p.key} className="border-b border-white/5 hover:bg-white/[0.02]">
                  <td className="px-4 py-2.5 sticky left-0 bg-ink-900">
                    <div className="text-slate-200 font-medium">{p.label}</div>
                    <div className="text-[10.5px] text-slate-600 mono">{p.key}</div>
                  </td>
                  {data.roles.map((role) => {
                    const checked = (data.effective[role] ?? []).includes(p.key);
                    const disabled = !p.lockable || savingRole === role;
                    return (
                      <td key={role} className="text-center px-3 py-2.5">
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={disabled}
                          onChange={() => toggle(role, p.key, p)}
                          aria-label={`${roleLabel(role)} can see ${p.label}`}
                          className="w-4 h-4 rounded accent-accent-500 disabled:opacity-40 cursor-pointer disabled:cursor-not-allowed"
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card p-4 text-[12px] text-slate-500 space-y-1">
        <div>
          <span className="text-slate-300 font-medium">Executive Manager</span> always has access to every page — that row is intentionally hidden.
        </div>
        <div>
          Greyed-out checkboxes are pages every authenticated user must see (e.g. Profile, Messages) — they can't be revoked.
        </div>
        <div className="pt-1">
          {data.roles.map((r) => (
            <span key={r} className="mr-3">
              <span className="text-slate-300 font-medium">{roleLabel(r)}</span>: {ROLE_DESCRIPTION[r] ?? ""}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
